import type {
  CommunicationAccountSummary,
  CommunicationConversationSummary,
  CommunicationMessageSummary,
  ConversationListResponse,
  FacebookPagesResponse,
  MarkConversationReadResponse,
  MessageListResponse,
  SyncResponse,
} from "@/types/communication";

import { requestData } from "@/api/api-config";

export async function getConversations(params?: {
  channel?: string;
  isRead?: boolean;
  cursor?: string;
}) {
  return requestData<ConversationListResponse>(
    "communication/conversations",
    "get",
    {
      searchParams: params,
    },
  );
}

export async function getConversation(id: string) {
  return requestData<ConversationListResponse["conversations"][0]>(
    `communication/conversations/${id}`,
  );
}

export async function getMessages(conversationId: string, cursor?: string) {
  return requestData<MessageListResponse>(
    `communication/conversations/${conversationId}/messages`,
    "get",
    { searchParams: cursor ? { cursor } : undefined },
  );
}

export async function sendMessage(
  conversationId: string,
  body: string,
  replyToMessageId?: string,
) {
  return requestData<CommunicationMessageSummary>(
    `communication/conversations/${conversationId}/messages`,
    "post",
    {
      json: {
        body,
        ...(replyToMessageId && { reply_to_message_id: replyToMessageId }),
      },
    },
    false,
  );
}

export async function markConversationRead(conversationId: string) {
  return requestData<MarkConversationReadResponse>(
    `communication/conversations/${conversationId}/read`,
    "put",
  );
}

export async function getUnreadCount() {
  return requestData<{ count: number }>("communication/unread-count");
}

export async function getAccounts() {
  return requestData<CommunicationAccountSummary[]>("communication/accounts");
}

export async function getConfig() {
  return requestData<{ igAppId: string; metaAppId: string }>(
    "communication/accounts/config",
  );
}

export async function connectInstagram(
  code: string,
  redirectUri: string,
  catchError = true,
) {
  return requestData<CommunicationAccountSummary[]>(
    "communication/accounts/instagram/connect",
    "post",
    { json: { code, redirectUri } },
    catchError,
  );
}

export async function getFacebookPages(code: string, redirectUri: string) {
  return requestData<FacebookPagesResponse>(
    "communication/accounts/facebook/pages",
    "post",
    { json: { code, redirectUri } },
  );
}

export async function connectFacebook(
  userAccessToken: string,
  selections: {
    pageId: string;
    connectMessenger: boolean;
    connectInstagram: boolean;
  }[],
) {
  return requestData<CommunicationAccountSummary[]>(
    "communication/accounts/facebook/connect",
    "post",
    { json: { userAccessToken, selections } },
  );
}

export async function sendMediaMessage(
  conversationId: string,
  file: File,
  body?: string,
) {
  const formData = new FormData();
  formData.append("file", file);
  if (body) formData.append("body", body);

  return requestData<CommunicationMessageSummary>(
    `communication/conversations/${conversationId}/media-message`,
    "post",
    { body: formData },
    false,
  );
}

export async function sendReaction(
  conversationId: string,
  messageId: string,
  emoji: string,
) {
  const formData = new FormData();
  formData.append("emoji", emoji);
  return requestData<{ status: string }>(
    `communication/conversations/${conversationId}/messages/${messageId}/react`,
    "post",
    { body: formData },
    false,
  );
}

export async function linkPatient(conversationId: string, patientId: string) {
  return requestData<CommunicationConversationSummary>(
    `communication/conversations/${conversationId}/link-patient`,
    "put",
    { json: { patientId } },
  );
}

export async function unlinkPatient(conversationId: string) {
  return requestData<CommunicationConversationSummary>(
    `communication/conversations/${conversationId}/link-patient`,
    "delete",
  );
}

export async function syncMessages(conversationId?: string) {
  return requestData<SyncResponse>("communication/sync", "post", {
    searchParams: conversationId ? { conversationId } : undefined,
  });
}

export async function disconnectAccount(accountId: string) {
  return requestData<{ status: string }>(
    `communication/accounts/${accountId}`,
    "delete",
  );
}

export interface OEmbedData {
  html: string | null;
  thumbnailUrl: string | null;
  authorName: string | null;
  providerName: string | null;
  width: number | null;
  height: number | null;
}

export async function getOEmbed(url: string) {
  return requestData<OEmbedData>("communication/oembed", "get", {
    searchParams: { url },
  });
}
